import React, { useRef } from "react";
import styled from "styled-components";
import {
  motion,
  MotionValue,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { color } from "../tokens";
import { Inner, Reveal, STATIC_MODE, useReveal } from "../primitives";
import TradingCard from "../TradingCard";
import { FOURMATION_SECTION, CardData } from "../data";
import EraHeader from "./EraHeader";
import qvDemo from "../../images/qv-iq-chatbot-demo.webp";

const Wrap = styled.div`
  padding-bottom: clamp(120px, 22vh, 240px);
`;

const Thread = styled.svg`
  display: block;
  width: 100%;
  height: 56px;
  margin-top: clamp(32px, 6vh, 64px);
  overflow: visible;
`;

const CardGrid = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(20px, 3vw, 40px);
`;

/* Cards hang off the thread at alternating depths and drift as the
   section scrolls through — beads on a wire. */
const ParallaxCard: React.FC<{
  card: CardData;
  index: number;
  progress: MotionValue<number>;
}> = ({ card, index, progress }) => {
  const reduced = useReducedMotion();
  const amplitude = index % 2 === 0 ? 26 : -34;
  const y = useTransform(progress, [0, 1], [amplitude, -amplitude]);
  const media =
    card.project.id === "qvego" ? (
      <img src={qvDemo} alt="QV / EGO AI chatbot demo" />
    ) : undefined;

  const inner = (
    <Reveal delay={index * 0.14}>
      <TradingCard card={card} media={media} />
    </Reveal>
  );

  if (STATIC_MODE || reduced) {
    return inner;
  }
  return <motion.div style={{ y, willChange: "transform" }}>{inner}</motion.div>;
};

/**
 * Section — 04 4Mation (2024–2025): "Then the AI era started." A thread
 * draws across, then the three cards settle onto it with alternating
 * scroll parallax.
 */
const FourMationSection: React.FC = () => {
  const reduced = useReducedMotion();
  const { ref, shown } = useReveal<SVGSVGElement>();
  const gridRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: gridRef,
    offset: ["start end", "end start"],
  });

  return (
    <Wrap data-era={3}>
      <Inner>
        <EraHeader
          era={FOURMATION_SECTION}
          statementInk="Then the AI era started."
          statementMuted="Tech lead on multi-agent chatbots, email automation and workflow systems."
        />
        <Thread
          ref={ref}
          viewBox="0 0 1200 56"
          preserveAspectRatio="none"
          aria-hidden
          role="presentation"
        >
          <motion.path
            d="M 8 48 C 260 8, 940 8, 1192 48"
            fill="none"
            stroke={color.ink}
            strokeOpacity={0.3}
            strokeWidth={1.5}
            initial={false}
            animate={{ pathLength: reduced || shown ? 1 : 0 }}
            transition={{ duration: 1.3, ease: "easeOut" }}
          />
        </Thread>
        <CardGrid ref={gridRef}>
          {FOURMATION_SECTION.cards.map((card, i) => (
            <ParallaxCard
              key={card.project.id}
              card={card}
              index={i}
              progress={scrollYProgress}
            />
          ))}
        </CardGrid>
      </Inner>
    </Wrap>
  );
};

export default FourMationSection;
